import { AllValidSplitValues } from "../../types/Decisions";
import { anyValidSplit, getValidSplitValues, isValidSplit } from "../../utils/shareSplits";
import { makeAutoObservable } from "mobx";
import {
  DbotEvent,
  DecisionContext,
  DbotEventDispatcher,
  extractSectionWithMaxScoreFromArticles,
} from "../common/commonDomain";

import { parseInputString } from "../../utils/numberInput";

import { TMessage } from "../../i18n/tt";
import { DecisionType } from "../../common/decisions";
import { ArticleSelectorModel, NumberFieldModel } from "../../common/commonForms";

export type SplitShare = {
  type: DecisionType.splitShare;
  ratio: number;
  shareCapitalParagraph?: number;
};

export const splitShare = (data?: Partial<SplitShare>): SplitShare => ({
  type: DecisionType.splitShare,
  ratio: data?.ratio ?? DEFAULT_SPLIT_RATIO,
  shareCapitalParagraph: data?.shareCapitalParagraph,
});

export const DEFAULT_SPLIT_RATIO = 1;

export class SplitShareForm {
  readonly type = DecisionType.splitShare;

  dispatch: DbotEventDispatcher;
  context: DecisionContext;
  data: SplitShare;

  ratio: NumberFieldModel;
  shareCapitalParagraph: ArticleSelectorModel;
  allValidSplitValues: AllValidSplitValues[];

  constructor(
    dispatch: DbotEventDispatcher,
    context: DecisionContext,
    data: SplitShare = splitShare(),
  ) {
    makeAutoObservable(this, { dispatch: false }, { autoBind: true });
    this.dispatch = dispatch;
    this.context = context;
    this.data = data;

    this.ratio = new NumberFieldModel({
      value: data.ratio,
      isPositive: true,
      isInteger: true,
      required: true,
    });

    this.shareCapitalParagraph = new ArticleSelectorModel(
      context.inputState?.articles,
      data.shareCapitalParagraph,
    );

    this.allValidSplitValues = [];
  }

  init() {
    if (this.data.shareCapitalParagraph === undefined) {
      this.dispatch({
        type: "dbot/explore-articles-of-association",
        articles: this.context.inputState?.articles,
      });
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/explore-articles-of-association-success":
        if (!this.shareCapitalParagraph.selected) {
          this.shareCapitalParagraph.setSelected(
            extractSectionWithMaxScoreFromArticles(event.data.results, "SHARE_CAPITAL"),
          );
        }
        break;
    }
  }

  get parsedRatio(): number | undefined {
    return parseInputString(this.ratio.inputValue, this.ratio.locale);
  }

  get currentNominalValue(): number {
    return this.context.inputState?.sharenominalValue ?? 0;
  }

  get newNominalValue(): number {
    const ratio = this.parsedRatio;
    if (ratio === undefined) {
      return this.currentNominalValue;
    }
    return this.currentNominalValue / ratio;
  }

  get articlesOfAssociation() {
    return this.context.inputState?.articles;
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return (
      this.ratio.isValid &&
      this.shareCapitalParagraph.isValid &&
      isValidSplit(this.currentNominalValue, this.ratio.toNumber()!)
    );
  }

  get showSplitIntoOneMessage(): boolean {
    return anyValidSplit(this.currentNominalValue) && this.parsedRatio === 1;
  }

  get invalidSplitMessage(): TMessage | undefined {
    const closestSplit = findClosestSplit(this);
    if (closestSplit) {
      return {
        key: "decisions.shareSplits.split.helperText-invalid",
        params: { n: closestSplit },
      };
    }
  }

  setSplitRatio(value: string) {
    this.ratio.setInputValue(value);
    const n = this.parsedRatio;
    if (n !== undefined) {
      const { splitRatio, allValidSplitValues } = changeSplitShareRatio(this, n);
      this.ratio.setInputValue(splitRatio);
      this.allValidSplitValues = allValidSplitValues;
    }
  }

  validate(): boolean {
    this.ratio.validate();

    return this.isValid;
  }
}

export function changeSplitShareRatio(form: SplitShareForm, value: number) {
  let splitRatio = value;
  let allValidSplitValues: AllValidSplitValues[] = [];

  if (!isNaN(splitRatio)) {
    if (splitRatio < 0) splitRatio = 1;
  }

  if (
    !isValidSplit(form.currentNominalValue, splitRatio) &&
    (!form.allValidSplitValues.length ||
      !form.allValidSplitValues.some((d) => d.currentNominalValue === form.currentNominalValue))
  ) {
    allValidSplitValues = [
      ...form.allValidSplitValues,
      {
        currentNominalValue: form.currentNominalValue,
        splitValues: getValidSplitValues(form.currentNominalValue),
      },
    ];
  }

  return {
    splitRatio,
    allValidSplitValues,
  };
}

export function findClosestSplit(form: SplitShareForm) {
  const ratio = form.parsedRatio;
  if (ratio === undefined) {
    return undefined;
  }
  return form.allValidSplitValues
    .find((splitValue) => splitValue.currentNominalValue === form.currentNominalValue)
    ?.splitValues.reduce((closest: number, value: number) =>
      Math.abs(value - ratio) < Math.abs(closest - ratio) ? value : closest,
    );
}
